import { useEffect, useState } from 'react';
import api from '../../utils/api';
import { FileText, DollarSign, Newspaper, Library as LibraryIcon, Bot, MessageSquare, ExternalLink, Phone, Copy, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import useAuthStore from '../../store/authStore';

const WHATSAPP_NUMBER = '255753541330';  // Tanzania: +255, without leading 0
const WHATSAPP_DISPLAY = '+255 753 541 330';

const Dashboard = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState({ minutes: 0, financial: 0, news: 0, library: 0 });
  const [recentNews, setRecentNews] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [minutes, financial, news, library] = await Promise.all([
        api.get('/minutes'),
        api.get('/financial-statements'),
        api.get('/news'),
        api.get('/library')
      ]);
      setStats({
        minutes: minutes.data.length,
        financial: financial.data.length,
        news: news.data.length,
        library: library.data.length
      });
      setRecentNews(news.data.slice(0, 3));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const copyPhoneNumber = () => {
    navigator.clipboard.writeText(WHATSAPP_DISPLAY);
    setCopied(true);
    toast.success('Phone number copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const openWhatsApp = () => {
    const message = `Hello Support, I need help with the AHSGA app.
Name: ${user?.full_name || 'Member'}
Role: ${user?.role || 'member'}
Email: ${user?.email || ''}
Issue: `;

    // Create a temporary link and click it - works better than window.open
    const link = document.createElement('a');
    link.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const statsCards = [
    { label: 'Minutes', value: stats.minutes, icon: FileText, link: '/member/minutes', color: 'bg-blue-100 text-blue-600' },
    { label: 'Financial Docs', value: stats.financial, icon: DollarSign, link: '/member/financial-statements', color: 'bg-green-100 text-green-600' },
    { label: 'News Articles', value: stats.news, icon: Newspaper, link: '/member/news', color: 'bg-orange-100 text-orange-600' },
    { label: 'Library Items', value: stats.library, icon: LibraryIcon, link: '/member/library', color: 'bg-purple-100 text-purple-600' },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-header">Member Dashboard</h1>
          <p className="text-slate-600">Welcome to AHSGA Portal, {user?.full_name?.split(' ')[0]}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link 
          to="/member/ai-assistant"
          className="flex items-center space-x-4 p-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl text-white hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg"
          data-testid="ai-assistant-btn"
        >
          <div className="bg-white/20 p-3 rounded-lg">
            <Bot className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold">AI Assistant</h3>
            <p className="text-sm text-white/80">Get help with portal questions</p>
          </div>
        </Link>
        
        {/* WhatsApp Support Card */}
        <div className="p-4 bg-gradient-to-r from-green-500 to-green-600 rounded-xl text-white shadow-lg">
          <div className="flex items-center space-x-4 mb-3">
            <div className="bg-white/20 p-3 rounded-lg">
              <MessageSquare className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">WhatsApp Support</h3>
              <p className="text-sm text-white/80">Chat with support directly</p>
            </div>
          </div>
          
          {/* Phone Number Display */}
          <div className="bg-white/10 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span className="font-mono font-semibold text-lg">{WHATSAPP_DISPLAY}</span>
              </div>
              <button 
                onClick={copyPhoneNumber}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Copy number"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={openWhatsApp}
              className="flex-1 flex items-center justify-center space-x-2 bg-white/20 hover:bg-white/30 rounded-lg py-2 px-3 transition-colors"
              data-testid="whatsapp-support-btn"
            >
              <span className="text-sm font-medium">Open WhatsApp</span>
              <ExternalLink className="w-4 h-4" />
            </button>
            <a
              href={`tel:${WHATSAPP_DISPLAY.replace(/\s/g, '')}`}
              className="flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg py-2 px-3 transition-colors"
              title="Call this number"
            >
              <Phone className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.label} to={stat.link} className="card hover:scale-[1.02] transition-transform cursor-pointer">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">{stat.label}</p>
                  <p className="text-3xl font-serif font-bold text-slate-900">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {recentNews.length > 0 && (
        <div>
          <h2 className="section-title mb-4">Latest News</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {recentNews.map((news) => (
              <div key={news.id} className="card">
                <h3 className="font-serif font-semibold text-lg text-slate-900 mb-2">{news.title}</h3>
                <p className="text-sm text-slate-600 line-clamp-2">{news.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;